import { useRef, useEffect, useMemo, useState, useCallback } from "react";
import { Spin, Table, Checkbox, Tooltip, Input } from "antd";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import {
    FIELD_IDS,
    toNumber,
    money,
    moneySigned,
    buildVODGridRows,
    buildGiftGridRows,
    buildOtherAssetGridRows,
    buildCashInHandRows,
    buildGroupedVodRows,
    normalizeYesNo,
    calcAgeYearsFromBirthDate,
    buildReservesJsonMerge,
    readReservesFlagsMapFromJson,
    readCashInHandFromJson,
    ASSET_TYPE_LABEL_MAP,
    isEligibleForReservesCalculator
} from "../../utils/requiredReservesHelper";
import { formatCurrencyTyping, enforceNonNegativeCurrency } from "../../utils/format";
import { getAccessToken } from "../../services/authApi";
import { getBorrowerPairs } from "../../services/borrowerApi";
import { hasPageAccess } from "../../services/personaApi";
import { fetchVODs, fetchGiftsGrants, fetchOtherAssets } from "../../services/loanSubCollectionApi";
import { fieldReader as fieldReaderApi, fieldWriter as fieldWriterApi } from "../../services/loanApi";
import { LOAN_PROGRAM_CODES } from "../../config/organizations";
import { isLoanProgram } from "../../utils/loanProgramUtil";

export default function RequiredReservesPage() {
    const ENABLE_CASH_IN_HAND = true;

    const [loanId, setLoanId] = useState("");
    const [loanNumber, setLoanNumber] = useState("");

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");

    const [token, setToken] = useState("");

    // auth + permissions
    const [authUser, setAuthUser] = useState(null);
    const [canSave, setCanSave] = useState(true);
    const [permissionMessage, setPermissionMessage] = useState("");

    // eligibility gating
    const [eligibilityChecked, setEligibilityChecked] = useState(false);
    const [isEligible, setIsEligible] = useState(false);
    const [eligibilityMessage, setEligibilityMessage] = useState("");

    // read-only fields needed for eligibility + cash-in-hand
    const [loanProgram1401, setLoanProgram1401] = useState(""); // 1401
    const [loanProgramCode, setLoanProgramCode] = useState(""); // CX.LOAN.PROGRAM.IDENTIFIER
    const [shortTermRental, setShortTermRental] = useState(""); // CX.Short.Term.Rental
    const [selectedProduct, setSelectedProduct] = useState(""); // CX.LOAN.PRODUCT.NAME
    const selectedProductRef = useRef("");

    const [cashInHand142, setCashInHand142] = useState(0); // field 142 (may be negative)

    // borrower pairs
    const [borrowerPairs, setBorrowerPairs] = useState([]);
    const [selectedPairLegacyId, setSelectedPairLegacyId] = useState("");
    const [selectedPair, setSelectedPair] = useState(null);
    const [applicationId, setApplicationId] = useState("");

    // VOD / Gifts / grid
    const [vodLoading, setVodLoading] = useState(false);
    const [vodsRaw, setVodsRaw] = useState([]);
    const [giftsRaw, setGiftsRaw] = useState([]);
    const giftsRawRef = useRef([]);

    const [otherAssetsRaw, setOtherAssetsRaw] = useState([]);
    const otherAssetsRawRef = useRef([]);

    const [gridRows, setGridRows] = useState([]);

    // saved JSON
    const [savedJsonObj, setSavedJsonObj] = useState(null);
    const savedJsonRef = useRef(null);

    const [ltv353, setLtv353] = useState(0);
    const [pitia912, setPitia912] = useState(0);


    // cash-in-hand reserves flag restore
    const [cashInHandUseForReserve, setCashInHandUseForReserve] = useState(false);

    const [reserveCopied, setReserveCopied] = useState(false);
    const [downCopied, setDownCopied] = useState(false);


    // toast
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("success");
    const [showToast, setShowToast] = useState(false);
    const initOnceRef = useRef(false);
    const [reserveCopyOpen, setReserveCopyOpen] = useState(false);
    const [downCopyOpen, setDownCopyOpen] = useState(false);
    const reserveCopyTimerRef = useRef(null);
    const downCopyTimerRef = useRef(null);

    const showToastMsg = (message, type = "success") => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
    };

    useEffect(() => {
        if (!showToast) return;

        setToastVisible(true);
        setToastAnimation("animate-fade-in-right");

        const hideTimer = setTimeout(() => {
            setToastAnimation("animate-fade-out-right");

            const vanishTimer = setTimeout(() => {
                setShowToast(false);
                setToastVisible(false);
            }, 500);

            return () => clearTimeout(vanishTimer);
        }, 4000);

        return () => clearTimeout(hideTimer);
    }, [showToast]);

    // read loan id + auth on mount
    useEffect(() => {
        const boot = async () => {
            try {
                if (window.elli?.script?.guest) {
                    window.elli.script.guest.create();
                    const loanObject = await window.elli.script.getObject("loan");
                    const loan = await loanObject.all();
                    setLoanId(loan?.loan?.id || "");
                    setLoanNumber(loan?.loan?.loanNumber || "");
                }
            } catch (e) {
                console.warn("Failed to read loan id:", e);
            }

            try {
                const authObj = await window.elli.script.getObject("auth");
                const user = await authObj.getUser();
                const allowed = await hasPageAccess("RequiredReservesPage", user);

                setCanSave(allowed);

                if (!allowed) {
                    setPermissionMessage("");
                } else {
                    setPermissionMessage("");
                }
            } catch (e) {
                console.warn("Failed to read auth user:", e);
                setCanSave(false);
                setPermissionMessage("");
            }
        };

        boot();
    }, []);

    const getOwnerAgeYearsMap = (pair) => {
        const bBirth = pair?.borrower?.birthDate || "";
        const cBirth = pair?.coborrower?.birthDate || "";

        return {
            Borrower: calcAgeYearsFromBirthDate(bBirth),
            CoBorrower: calcAgeYearsFromBirthDate(cBirth)
        };
    };

    const formatPairLabel = (pair) => {
        return pair?.coborrower?.firstName
            ? `${pair.borrower?.fullName} & ${pair.coborrower?.fullName}`
            : pair.borrower?.fullName || "";
    };

    const fetchEligibilityFields = useCallback(
        async (t, theLoanId) => {
            setVodLoading(true);
            try {
                const payload = [
                    FIELD_IDS.loanProgram_1401, // 1401
                    FIELD_IDS.loanProgram, // CX.LOAN.PROGRAM.IDENTIFIER
                    FIELD_IDS.shortTermRental, // CX.Short.Term.Rental
                    FIELD_IDS.loanProductName, // CX.LOAN.PRODUCT.NAME
                    FIELD_IDS.assetDeplVodJson, // CX.ASSET.DEPL.VOD.JSON
                    FIELD_IDS.cashInHand_142, // 142
                    FIELD_IDS.ltv_353,
                    FIELD_IDS.pitia_912
                ];

                const data = await fieldReaderApi(t, payload, theLoanId);
                const fieldsMap = data?.fields || data || {};

                const lp1401 = String(fieldsMap[FIELD_IDS.loanProgram_1401] ?? "").trim();
                const progCode = String(fieldsMap[FIELD_IDS.loanProgram] ?? "").trim();
                const strVal = normalizeYesNo(fieldsMap[FIELD_IDS.shortTermRental]);
                const prod = String(fieldsMap[FIELD_IDS.loanProductName] ?? "").trim();

                setLoanProgram1401(lp1401);
                setLoanProgramCode(progCode);
                setShortTermRental(strVal);

                selectedProductRef.current = prod;
                setSelectedProduct(prod);

                // cash-in-hand (142) can be negative
                const cash142 = toNumber(fieldsMap[FIELD_IDS.cashInHand_142]);
                setCashInHand142(cash142);

                setLtv353(toNumber(fieldsMap[FIELD_IDS.ltv_353]));
                setPitia912(toNumber(fieldsMap[FIELD_IDS.pitia_912]));

                // parse json
                const jsonText = fieldsMap[FIELD_IDS.assetDeplVodJson] ?? "";
                let parsed = null;
                try {
                    parsed = jsonText ? JSON.parse(jsonText) : null;
                } catch {
                    parsed = null;
                }
                setSavedJsonObj(parsed);
                savedJsonRef.current = parsed;

                // restore cash-in-hand reserves flag from JSON
                const cashInHandNode = readCashInHandFromJson(parsed, applicationId);
                setCashInHandUseForReserve(!!cashInHandNode?.useForReserve);

                // eligibility
                const elig = isEligibleForReservesCalculator({
                    loanProgram1401: lp1401,
                    loanProgramCode: progCode,
                    selectedProduct: prod
                });

                setEligibilityChecked(true);
                setIsEligible(elig.ok);
                setEligibilityMessage(elig.message || "");

                return { ok: elig.ok, cash142 };
            } catch (e) {
                console.error("Eligibility FieldReader error:", e);
                showToastMsg("Failed to load required fields.", "error");
                setEligibilityChecked(true);
                setIsEligible(false);
                setEligibilityMessage("Required Reserves Calculator is not allowed for this loan program.");
                return false;
            }
        },
        [applicationId]
    );

    const loadVodsForApplication = useCallback(
        async (t, theLoanId, appId, ownerAgeYearsMap, pairForGifts, cash142) => {
            setVodLoading(true);

            try {
                if (!appId) {
                    setVodsRaw([]);
                    setGiftsRaw([]);
                    giftsRawRef.current = [];
                    setOtherAssetsRaw([]);
                    otherAssetsRawRef.current = [];
                    setGridRows([]);
                    return;
                }

                const [vods, gifts, otherAssets] = await Promise.all([
                    fetchVODs(t, theLoanId, appId),
                    fetchGiftsGrants(t, theLoanId, appId),
                    fetchOtherAssets(t, theLoanId, appId)
                ]);

                const rawVods = Array.isArray(vods) ? vods : [];
                setVodsRaw(rawVods);

                const rawGifts = Array.isArray(gifts) ? gifts : [];
                setGiftsRaw(rawGifts);
                giftsRawRef.current = rawGifts;

                const rawOtherAssets = Array.isArray(otherAssets) ? otherAssets : [];
                setOtherAssetsRaw(rawOtherAssets);
                otherAssetsRawRef.current = rawOtherAssets;

                const sel = selectedProductRef.current || "";

                const vodRows = buildVODGridRows(rawVods, ownerAgeYearsMap, sel);
                const otherAssetRows = buildOtherAssetGridRows(rawOtherAssets, sel);
                const giftRows = buildGiftGridRows(rawGifts, pairForGifts);

                const cashInHandRows = ENABLE_CASH_IN_HAND
                    ? buildCashInHandRows(cash142, selectedProductRef.current)
                    : [];

                const allRows = [
                    ...vodRows,
                    ...otherAssetRows,
                    ...giftRows,
                    ...cashInHandRows
                ];

                const flagsMap = readReservesFlagsMapFromJson(savedJsonRef.current, appId);

                const restored = allRows.map((r) => {
                    const saved = flagsMap[r.key];

                    if (r.isCashInHand) {
                        return {
                            ...r,
                            verified: false,
                            useForReserve: true,
                            useForDownPayment: false,
                            downPaymentAmount: ""
                        };
                    }

                    if (r.isGift) {
                        const verified = !!saved?.verified;
                        const useForDownPayment = !!saved?.useForDownPayment;
                        const defaultAmt = verified ? toNumber(r.cashValue) : 0;
                        const savedAmt = toNumber(saved?.downPaymentAmount);

                        return {
                            ...r,
                            verified,
                            useForReserve: false,
                            useForDownPayment,
                            downPaymentAmount: useForDownPayment
                                ? (savedAmt > 0 ? money(savedAmt) : money(defaultAmt))
                                : ""
                        };
                    }

                    const verified = !!saved?.verified;
                    const useForReserve = !!saved?.useForReserve;
                    const useForDownPayment = !!saved?.useForDownPayment;
                    const defaultAmt = verified ? toNumber(r.cashValue) : 0;
                    const savedAmt = toNumber(saved?.downPaymentAmount);

                    return {
                        ...r,
                        verified,
                        useForReserve,
                        useForDownPayment,
                        downPaymentAmount: useForDownPayment
                            ? (savedAmt > 0 ? money(savedAmt) : money(defaultAmt))
                            : ""
                    };
                });

                setGridRows(restored);
                setCashInHandUseForReserve(true);
            } catch (e) {
                console.error("VOD/Gifts/Other Assets load error:", e);
                showToastMsg("Failed to load VODs / Gifts / Other Assets.", "error");

                setVodsRaw([]);
                setGiftsRaw([]);
                giftsRawRef.current = [];
                setOtherAssetsRaw([]);
                otherAssetsRawRef.current = [];
                setGridRows([]);
            } finally {
                setVodLoading(false);
            }
        },
        [cashInHand142]
    );

    const loadBorrowerPairs = useCallback(
        async (t, theLoanId, cash142) => {
            try {
                const pairs = await getBorrowerPairs(t, theLoanId);
                const list = Array.isArray(pairs) ? pairs : [];
                setBorrowerPairs(list);

                if (list.length) {
                    const first = list[0];
                    setSelectedPair(first);
                    setSelectedPairLegacyId(first.legacyId || "");
                    setApplicationId(first.id || "");

                    const ownerAgeYearsMap = getOwnerAgeYearsMap(first);
                    await loadVodsForApplication(t, theLoanId, first.id, ownerAgeYearsMap, first, cash142);
                } else {
                    setSelectedPair(null);
                    setSelectedPairLegacyId("");
                    setApplicationId("");
                    setVodsRaw([]);
                    setGiftsRaw([]);
                    giftsRawRef.current = [];
                    setOtherAssetsRaw([]);
                    otherAssetsRawRef.current = [];
                    setGridRows([]);
                    showToastMsg("No borrower pairs found.", "error");
                }
            } catch (e) {
                console.error("Borrower pairs load error:", e);
                showToastMsg("Failed to load borrower pairs.", "error");
                setBorrowerPairs([]);
                setSelectedPair(null);
                setSelectedPairLegacyId("");
                setApplicationId("");
                setVodsRaw([]);
                setGiftsRaw([]);
                giftsRawRef.current = [];
                setOtherAssetsRaw([]);
                otherAssetsRawRef.current = [];
                setGridRows([]);
            }
        },
        [loadVodsForApplication]
    );

    // init once loanId ready
    useEffect(() => {
        if (!loanId) return;
        if (initOnceRef.current) return;
        initOnceRef.current = true;

        const init = async () => {
            setLoading(true);
            setLoadingText("Loading Required Reserves...");

            try {
                const authObj = await window.elli.script.getObject("auth");
                const authCode = await authObj.createAuthCode();
                const t = await getAccessToken(authCode);
                setToken(t);

                const eligResult = await fetchEligibilityFields(t, loanId);

                if (eligResult?.ok) {
                    await loadBorrowerPairs(t, loanId, eligResult.cash142);
                } else {
                    setBorrowerPairs([]);
                    setSelectedPair(null);
                    setSelectedPairLegacyId("");
                    setApplicationId("");
                    setVodsRaw([]);
                    setGiftsRaw([]);
                    giftsRawRef.current = [];
                    setGridRows([]);
                }
                setLoading(false);
                setLoadingText("");
            } catch (e) {
                console.error("Init error:", e);
                setLoading(false);
                setLoadingText("");
            }
        };

        init();
    }, [loanId, fetchEligibilityFields, loadBorrowerPairs]);

    const showBlockedScreen = eligibilityChecked && !isEligible;
    const onBlockedMessage = useMemo(() => {
        if (!eligibilityChecked) return "";
        if (isEligible) return "";
        return eligibilityMessage || "Required Reserves Calculator is not allowed for this loan program.";
    }, [eligibilityChecked, isEligible, eligibilityMessage]);

    // verifiedAsset = max(cash - (auAmount / auPercent), 0)
    // allowedReserve = verified ? (verifiedAsset * reservePercent) : 0
    const computedRows = useMemo(() => {
        return (gridRows || []).map((r) => {
            const reservePct = Number(r.reservePercent ?? 0);
            const reservePctSafe = Number.isFinite(reservePct) ? reservePct : 0;

            if (r.isCashInHand) {
                const cashAbs = Math.abs(toNumber(r.cashInHandValue));
                const allowedReserve = cashAbs > 0 ? (cashAbs * reservePctSafe) : 0; // NEW

                return {
                    ...r,
                    verifiedAsset: 0,
                    allowedReserve
                };
            }

            const cash = toNumber(r.cashValue);
            const auAmount = toNumber(r.amountUsedAu);
            const auPct = Number(r.auPercent ?? 0);

            const auPctSafe = Number.isFinite(auPct) ? auPct : 0;

            const base = auAmount > 0 && auPctSafe > 0 ? cash - auAmount / auPctSafe : cash;
            const verifiedAsset = Math.max(toNumber(base), 0);

            const allowedReserve = r.verified ? verifiedAsset * reservePctSafe : 0;

            return {
                ...r,
                verifiedAsset,
                allowedReserve
            };
        });
    }, [gridRows]);


    const groupedRows = useMemo(() => buildGroupedVodRows(computedRows, toNumber), [computedRows]);

    const totalCashValue = useMemo(() => {
        return (computedRows || []).reduce((s, r) => {
            if (r.isGroup || r.isSubtotal) return s;
            if (r.isCashInHand) return s + Math.abs(toNumber(r.cashInHandValue)); // show under Cash Or Market Value
            return s + toNumber(r.cashValue);
        }, 0);
    }, [computedRows]);

    const totalAuAmount = useMemo(() => {
        return (computedRows || []).reduce((s, r) => s + (r.isGroup || r.isSubtotal ? 0 : toNumber(r.amountUsedAu)), 0);
    }, [computedRows]);

    const getAllowedReservesDisplayAmount = (row) => {
        if (!row) return 0;
        if (row.isGroup || row.isSubtotal) return 0;
        if (row.isCashInHand) return 0;
        if (!row.verified) return 0;

        if (!row.useForReserve) return 0;

        const allowed = toNumber(row.allowedReserve);
        const dp = toNumber(row.downPaymentAmount);

        if (row.useForReserve && row.useForDownPayment) {
            return Math.max(allowed - dp, 0);
        }

        return allowed;
    };

    const totalAllowedReserve = useMemo(() => {
        return (computedRows || []).reduce((s, r) => {
            if (r.isGroup || r.isSubtotal) return s;
            return s + getAllowedReservesDisplayAmount(r);
        }, 0);
    }, [computedRows]);


    const totalVerifiedAsset = useMemo(() => {
        return (computedRows || []).reduce((s, r) => {
            if (r.isGroup || r.isSubtotal) return s;

            if (r.isCashInHand) return s;

            return r.verified ? s + toNumber(r.verifiedAsset) : s;
        }, 0);
    }, [computedRows]);

    // reserve allocation amount for totals + reserve sources
    const getReserveAllocationAmount = (row) => {
        if (!row) return 0;
        if (row.isGroup || row.isSubtotal) return 0;
        if (row.isGift) return 0;

        if (row.isCashInHand) {
            return row.useForReserve ? toNumber(row.allowedReserve) : 0;
        }

        if (!row.verified) return 0;

        // if Reserves? is not checked, do not count in reserve total
        if (!row.useForReserve) return 0;

        const allowed = toNumber(row.allowedReserve);
        const dp = toNumber(row.downPaymentAmount);

        // both checked -> reserve gets remaining amount after DP
        if (row.useForDownPayment) {
            return Math.max(allowed - dp, 0);
        }

        return allowed;
    };


    const totalVerifiedAssetReserve = useMemo(() => {
        return (computedRows || []).reduce((s, r) => {
            if (r.isGroup || r.isSubtotal) return s;
            return s + getReserveAllocationAmount(r);
        }, 0);
    }, [computedRows]);


    const totalVerifiedAssetDownPayment = useMemo(() => {
        return (computedRows || []).reduce((s, r) => {
            if (r.isGroup || r.isSubtotal) return s;
            if (r.isCashInHand) return s;
            if (!r.useForDownPayment) return s;
            if (!r.verified) return s;

            const entered = toNumber(r.downPaymentAmount);

            // if checkbox checked but textbox empty/0 -> do NOT count anything yet
            if (entered > 0) return s + entered;

            return s;
        }, 0);
    }, [computedRows]);



    const onVerifiedChange = (rowKey, checked) => {
        setGridRows((prev) =>
            (prev || []).map((r) => {
                if (r.key !== rowKey) return r;

                if (!checked) {
                    return {
                        ...r,
                        verified: false,
                        useForReserve: false,
                        useForDownPayment: false,
                        downPaymentAmount: "" // clear
                    };
                }

                if (r.isGift) {
                    return { ...r, verified: true, useForReserve: false, useForDownPayment: false, downPaymentAmount: "" };
                }

                return { ...r, verified: true, useForReserve: true, useForDownPayment: false, downPaymentAmount: "" };
            })
        );
    };

    const onUseForReserveChange = (rowKey, checked) => {
        setGridRows((prev) =>
            (prev || []).map((r) => {
                if (r.key !== rowKey) return r;

                if (r.isCashInHand) {
                    setCashInHandUseForReserve(true);
                    return { ...r, useForReserve: true };
                }

                // no longer forces Down Payment off
                return {
                    ...r,
                    useForReserve: checked
                };
            })
        );
    };


    const onUseForDownPaymentChange = (rowKey, checked) => {
        setGridRows((prev) =>
            (prev || []).map((r) => {
                if (r.key !== rowKey) return r;

                if (!checked) {
                    return { ...r, useForDownPayment: false, downPaymentAmount: "" };
                }

                // user decides the amount, start blank/0
                return {
                    ...r,
                    useForDownPayment: true,
                    // do NOT force reserve off anymore (new requirement)
                    // useForReserve: r.useForReserve,
                    downPaymentAmount: "" // or "0.00" if you prefer
                };
            })
        );
    };


    const onDownPaymentAmountChange = (rowKey, value) => {
        setGridRows((prev) =>
            (prev || []).map((r) => {
                if (r.key !== rowKey) return r;
                return { ...r, downPaymentAmount: value };
            })
        );
    };

    async function syncHostLoanWorkspace() {
        try {
            const loanObj = await window.elli.script.getObject("loan");
            await loanObj.merge();
            await loanObj.calculate();
        } catch (e) {
            console.warn("Host loan merge/calc failed:", e);
        }
    }

    const onSave = async () => {
        if (!token || !loanId || !applicationId) {
            showToastMsg("Missing token/loan/application.", "error");
            return;
        }

        if (!canSave) {
            showToastMsg(permissionMessage || "You don’t have permission to save.", "error");
            return;
        }

        if (!isEligible) {
            showToastMsg(onBlockedMessage || "Required Reserves Calculator is not allowed.", "error");
            return;
        }

        try {
            setLoading(true);
            setLoadingText("Saving Required Reserves...");

            // merge reserves rows + gifts + cash-in-hand into CX.ASSET.DEPL.VOD.JSON
            const mergedJson = buildReservesJsonMerge(
                savedJsonRef.current,
                applicationId,
                computedRows,
                giftsRawRef.current,
                cashInHand142,
                cashInHandUseForReserve,
                otherAssetsRawRef.current
            );
            const jsonText = JSON.stringify(mergedJson);

            const months = calcReserveMonths({
                productName: selectedProductRef.current || selectedProduct,
                programId: loanProgramCode,
                ltv: ltv353,
                verifiedAssetsReserve: totalVerifiedAssetReserve,
                verifiedAssetsDownPayment: totalVerifiedAssetDownPayment,
                cashFromToBorrower: cashInHand142,
                pitia: pitia912,

            });


            const writerPayload = [
                { id: FIELD_IDS.assetDeplVodJson, value: jsonText },
                { id: FIELD_IDS.verifiedAsset, value: String(toNumber(totalVerifiedAsset)) },
                { id: FIELD_IDS.verifiedAssetReserve, value: String(toNumber(totalVerifiedAssetReserve)) },
                { id: FIELD_IDS.verifiedAssetDownPayment, value: String(toNumber(totalVerifiedAssetDownPayment)) },
                { id: FIELD_IDS.reservesActual, value: months.actual },      // "" if NOT allowed
                { id: FIELD_IDS.reservesNoCashout, value: months.noCashout } // always rounded 1 dec
            ];

            await fieldWriterApi(token, loanId, writerPayload);
            await syncHostLoanWorkspace();

            savedJsonRef.current = mergedJson;
            setSavedJsonObj(mergedJson);

            showToastMsg("Required Reserves saved successfully.", "success");
        } catch (err) {
            console.error("Save failed:", err);

            let msg =
                err?.response?.data?.details ||
                err?.response?.data?.summary ||
                err?.message ||
                "Save failed.";

            if (loanNumber) {
                msg = msg.replace(/Loan\s+'[a-f0-9-]+'/i, `Loan '${loanNumber}'`);
            }

            showToastMsg(msg, "error");
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    const toSingleLine = (s) =>
        String(s ?? "")
            .replace(/[\r\n]+/g, " ")     // kill newlines
            .replace(/\s+/g, " ")        // collapse whitespace
            .trim();

    const formatSourceLineText = (row, mode) => {
        // mode: "reserve" | "down"

        if (!row) return "";

        // ---------- Gift / Grant ----------
        if (row.isGift) {
            const type = row.typeLabel || row.giftAssetType || "Gift";

            // Reserve uses Allowed Reserves; Down uses ONLY user-entered amount
            const amtReserve = toNumber(row.allowedReserve);
            const amtDown = toNumber(row.downPaymentAmount);

            const amt = mode === "reserve" ? amtReserve : amtDown;
            return toSingleLine(`${type} - $ ${money(amt)}`);
        }

        // ---------- Cash in Hand ----------
        if (row.isCashInHand) {
            // reserves only — show allowed reserves (pct applied)
            const amtReserve = toNumber(row.allowedReserve);
            const amt = mode === "reserve" ? amtReserve : 0;
            return toSingleLine(`Cash in Hand - $ ${money(amt)}`);
        }

        // ---------- Normal VOD rows ----------
        const type = toSingleLine(row.typeLabel || "");

        const holderRaw = toSingleLine(row.holderName || "");
        const bank = holderRaw.includes(" - ")
            ? toSingleLine(holderRaw.split(" - ")[0])
            : holderRaw;

        const acct = toSingleLine(row.accountIdentifier || "");

        // Reserve amount:
        // - If both checked => remaining reserves (allowed - downPayment)
        // - If reserves only => allowed
        // - If down payment only => downPaymentAmount (per your rule for totals)
        const allowed = toNumber(row.allowedReserve);
        const dp = toNumber(row.downPaymentAmount);

        let amtReserve = 0;
        if (row.useForReserve) {
            amtReserve = row.useForDownPayment ? Math.max(allowed - dp, 0) : allowed;
        } else if (row.useForDownPayment) {
            amtReserve = Math.max(dp, 0);
        }

        // Down amount always equals user-entered amount (no fallback)
        const amtDown = dp;

        const amount = mode === "reserve" ? amtReserve : amtDown;

        return toSingleLine(
            `${type}${bank ? ` - ${bank}` : ""}${acct ? ` - ${acct}` : ""} - $ ${money(amount)}`
        );
    };

    const reserveSources = useMemo(() => {
        return (computedRows || [])
            .filter((r) => !r.isGroup && !r.isSubtotal)
            .filter((r) => !r.isGift)
            .filter((r) => r.useForReserve) // still only items with Reserves? checked appear here
            .filter((r) => (r.isCashInHand ? true : r.verified))
            .filter((r) => {
                const amt = getReserveAllocationAmount(r); // remaining reserve allocation
                return amt > 0;
            })
            .map((r) => ({ key: r.key, text: formatSourceLineText(r, "reserve") }));
    }, [computedRows]);

    const downPaymentSources = useMemo(() => {
        return (computedRows || [])
            .filter((r) => !r.isGroup && !r.isSubtotal)
            .filter((r) => r.useForDownPayment)
            .filter((r) => !r.isCashInHand)
            .filter((r) => r.verified)
            .filter((r) => {
                const dp = toNumber(r.downPaymentAmount);

                // Do not show any Down Payment source until user enters a positive amount
                return dp > 0;
            })
            .map((r) => ({ key: r.key, text: formatSourceLineText(r, "down") }));
    }, [computedRows]);


    const reserveSourcesLine = useMemo(
        () => reserveSources.map(x => toSingleLine(x.text)).join("; "),
        [reserveSources]
    );

    const downPaymentSourcesLine = useMemo(
        () => downPaymentSources.map(x => toSingleLine(x.text)).join("; "),
        [downPaymentSources]
    );

    const columns = [
        {
            title: "Account Type",
            dataIndex: "typeLabel",
            key: "typeLabel",
            width: "15%",
            render: (val, row) => {
                if (row.isGroup) {
                    return {
                        children: (
                            <span>
                                {val}
                                {row.groupInfo ? (
                                    <span className="group-inline-info"> {row.groupInfo}</span>
                                ) : null}
                            </span>
                        ),
                        props: { colSpan: 9 }
                    };
                }

                if (row.isSubtotal) {
                    return { children: <span>Subtotal</span>, props: { colSpan: 3 } };
                }

                if (row.isGift || row.isOtherAsset) {
                    const apiValue = row.giftAssetType || row.otherAssetType || val;
                    return ASSET_TYPE_LABEL_MAP[apiValue] || apiValue || "";
                }

                return val;
            }
        },
        {
            title: "Account Details",
            dataIndex: "holderName",
            key: "holderName",
            width: "11%",
            render: (val, row) =>
                row.isGroup || row.isSubtotal
                    ? { children: null, props: { colSpan: 0 } }
                    : val || ""
        },
        {
            title: "Account Owner",
            dataIndex: "depositoryAccountName",
            key: "depositoryAccountName",
            width: "12%",
            render: (val, row) =>
                row.isGroup || row.isSubtotal
                    ? { children: null, props: { colSpan: 0 } }
                    : val || ""
        },
        {
            title: "Cash Or Market Value",
            dataIndex: "cashValue",
            key: "cashValue",
            width: "10%",
            align: "left",
            render: (v, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isCashInHand)
                    return `$ ${money(Math.abs(toNumber(row.cashInHandValue)))}`;
                return `$ ${money(v)}`;
            }
        },
        {
            title: "Used in AU",
            dataIndex: "amountUsedAu",
            key: "usedInAu",
            width: "7%",
            align: "center",
            render: (v, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isSubtotal) return "";
                if (row.isGift) return "No";
                if (row.isCashInHand) return "";
                return toNumber(v) > 0 ? "Yes" : "No";
            }
        },
        {
            title: "AU Amount",
            dataIndex: "amountUsedAu",
            key: "amountUsedAu",
            width: "9%",
            align: "left",
            render: (v, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isCashInHand) return "";
                if (row.isGift) return "";
                return `$ ${money(v)}`;
            }
        },
        {
            title: "Verified",
            dataIndex: "verified",
            key: "verified",
            width: "4%",
            align: "center",
            render: (_, row) => {
                if (row.isGroup) return null;
                if (row.isSubtotal) return "";
                if (row.isCashInHand) return null;
                return (
                    <Checkbox
                        checked={!!row.verified}
                        onChange={(e) => onVerifiedChange(row.key, e.target.checked)}
                    />
                );
            }
        },
        {
            title: "Verified Asset",
            dataIndex: "verifiedAsset",
            key: "verifiedAsset",
            width: "9%",
            align: "left",
            render: (v, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isSubtotal) return <span>{`$ ${money(v)}`}</span>;
                if (row.isCashInHand) return "";
                return row.verified ? `$ ${money(v)}` : "";
            }
        },
        {
            title: "Allowed Reserves",
            dataIndex: "allowedReserve",
            key: "allowedReserve",
            width: "9%",
            align: "left",
            render: (v, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isCashInHand) return "";
                if (row.isSubtotal) return <span>{`$ ${money(v)}`}</span>;
                const display = getAllowedReservesDisplayAmount(row);
                return row.verified ? `$ ${money(display)}` : "";
            }
        },
        {
            title: "Reserves?",
            dataIndex: "useForReserve",
            key: "useForReserve",
            width: "6%",
            align: "center",
            render: (_, row) => {
                if (row.isGroup) return null;
                if (row.isSubtotal) return "";
                if (row.isGift) return null;

                // ONLY Cash-in-hand is forced + disabled
                if (row.isCashInHand) {
                    return <Checkbox checked={true} disabled={true} />;
                }

                // All other assets behave normally (not disabled unless not verified)
                return (
                    <Checkbox
                        checked={!!row.useForReserve}
                        disabled={!row.verified}
                        onChange={(e) => onUseForReserveChange(row.key, e.target.checked)}
                    />
                );
            }
        },
        {
            title: "Down Payment?",
            dataIndex: "useForDownPayment",
            key: "useForDownPayment",
            width: "8%",
            align: "center",
            render: (_, row) => {
                if (row.isGroup) return null;
                if (row.isSubtotal) return "";
                if (row.isCashInHand) return null;

                const reservePct = Number(row.reservePercent ?? 0);
                const allowDownPayment = Number.isFinite(reservePct) && reservePct >= 1.0;

                return (
                    <Checkbox
                        checked={!!row.useForDownPayment}
                        disabled={!row.verified || !allowDownPayment}
                        onChange={(e) => onUseForDownPaymentChange(row.key, e.target.checked)}
                    />
                );
            }
        },
        {
            title: "Down Payment Amount",
            dataIndex: "downPaymentAmount",
            key: "downPaymentAmount",
            width: "10%",
            align: "left",
            render: (_, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isSubtotal) return <span>{`$ ${money(row.downPaymentAmount)}`}</span>;
                if (row.isCashInHand) return null;

                const enabled = !!row.useForDownPayment && !!row.verified;
                const max = toNumber(row.allowedReserve);

                return (
                    <div className="amount-input-wrapper amount-left">
                        <span className="currency-symbol">$</span>
                        <Input
                            value={row.downPaymentAmount || ""}
                            disabled={!enabled}
                            onChange={(e) => {
                                const typed = enforceNonNegativeCurrency(e.target.value);
                                const typedNum = toNumber(typed);

                                if (typedNum > max) {
                                    onDownPaymentAmountChange(row.key, formatCurrencyTyping(String(max)));
                                    showToastMsg(`Down Payment Amount cannot exceed Verified Asset ($ ${money(max)}).`, "error");
                                    return;
                                }

                                onDownPaymentAmountChange(row.key, typed);
                            }}
                            onBlur={(e) => {
                                const finalVal = enforceNonNegativeCurrency(e.target.value, true);
                                let num = toNumber(finalVal);

                                if (num > max) {
                                    num = max;
                                    showToastMsg(`Down Payment Amount cannot exceed Verified Asset ($ ${money(max)}).`, "error");
                                }

                                onDownPaymentAmountChange(row.key, num ? money(num) : "");
                            }}
                            placeholder="0.00"
                            className="form-input w-full text-sm border rounded p-1"
                        />
                    </div>
                );
            }
        }

    ];

    useEffect(() => {
        return () => {
            if (reserveCopyTimerRef.current) clearTimeout(reserveCopyTimerRef.current);
            if (downCopyTimerRef.current) clearTimeout(downCopyTimerRef.current);
        };
    }, []);

    const copyWithBubble = async (text, setOpen, timerRef) => {
        const value = String(text ?? "").trim();
        if (!value) return;

        const showBubble = () => {
            setOpen(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setOpen(false), 1000);
        };

        try {
            // Clipboard API works only on HTTPS / allowed context
            if (navigator?.clipboard?.writeText && window.isSecureContext) {
                await navigator.clipboard.writeText(value);
                showBubble();
                return;
            }
        } catch {
            // fall through to execCommand fallback
        }

        // Fallback (works in more embedded/iframe contexts)
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);

        ta.focus();
        ta.select();

        try {
            const ok = document.execCommand("copy");
            if (ok) showBubble();
        } finally {
            document.body.removeChild(ta);
        }
    };

    const round1 = (n) => Math.max(0, Math.round(toNumber(n) * 10) / 10);

    function calcReserveMonths({
        productName,
        programId,
        ltv,
        verifiedAssetsReserve,
        verifiedAssetsDownPayment,
        cashFromToBorrower,
        pitia
    }) {
        const pit = toNumber(pitia);
        if (!pit) return { actual: "", noCashout: "" };

        const vReserve = toNumber(verifiedAssetsReserve) + toNumber(verifiedAssetsDownPayment);
        const cash142 = toNumber(cashFromToBorrower);
        const ltvVal = toNumber(ltv);

        // Formula A
        const formulaA = (vReserve - cash142) / pit;

        // Formula A-1 (If 142 is Negative -> vReserve/912 else Formula A)
        const formulaA1 = cash142 < 0 ? (vReserve / pit) : formulaA;

        // Formula B (no cash-out months)
        const monthReserveNotFromCashout =
            cash142 < 0
                ? ((vReserve + cash142) / pit)
                : ((vReserve - cash142) / pit);

        const nonCashoutMonths = round1(monthReserveNotFromCashout);

        const prog = String(programId || "").trim().toUpperCase();

        /*
         * Mirrors calcReserveMonths() in src/utils/requiredReservesHelper.js -
         * keep the two in step.
         */
        const isALLY = isLoanProgram(productName, [
            LOAN_PROGRAM_CODES.PROGRAM_001, // ALLY (ChampsTPO only)
            LOAN_PROGRAM_CODES.PROGRAM_011  // ALLY EQUITY FIRST (ChampsTPO only)
        ]);

        const isSJ = isLoanProgram(productName, LOAN_PROGRAM_CODES.PROGRAM_008); // SUPER JUMBO / STARRY

        const isBPACC58 = prog === "BPACC58";
        const isBITIN = prog === "BITIN";

        let monthActualReserve;

        if (isSJ || isBPACC58) {
            monthActualReserve = formulaA1;
        } else if (isALLY) {
            if (ltvVal <= 50) {
                monthActualReserve = formulaA1;
            } else {
                if (nonCashoutMonths >= 2) monthActualReserve = formulaA1;
                else return { actual: "", noCashout: String(nonCashoutMonths) };
            }
        } else if (isBITIN) {
            if (nonCashoutMonths >= 3) monthActualReserve = formulaA1;
            else return { actual: "", noCashout: String(nonCashoutMonths) };
        } else {
            monthActualReserve = formulaA1;
        }

        const actualMonths = round1(monthActualReserve);

        return {
            actual: String(actualMonths),
            noCashout: String(nonCashoutMonths)
        };
    }

    const reserveMonths = useMemo(() => {
        return calcReserveMonths({
            productName: selectedProductRef.current || selectedProduct,
            programId: loanProgramCode,
            ltv: ltv353,
            verifiedAssetsReserve: totalVerifiedAssetReserve,
            verifiedAssetsDownPayment: totalVerifiedAssetDownPayment,
            cashFromToBorrower: cashInHand142,
            pitia: pitia912
        });
    }, [selectedProduct, loanProgramCode, ltv353, totalVerifiedAssetReserve, cashInHand142, pitia912]);



    return (
        <div className="rr-page space-y-6 p-25px">
            <LoadingOverlay loading={loading} text={loadingText} />

            <div className="rounded-lg p-4 space-y-6 max-w-8xl mx-auto">
                <div className="flex items-center justify-between">
                    <div className="page-title1">Required Reserves Calculator</div>

                    <div className="flex items-center gap-3">
                        {!showBlockedScreen && (
                            <>
                                <div className="save-hint">
                                    {canSave ? (
                                        <>
                                            Click <b>Calculate &amp; Save</b> to store total Verified Asset
                                        </>
                                    ) : (
                                        <span className="text-red-600">{permissionMessage}</span>
                                    )}
                                </div>

                                <button
                                    className="btn-base btn-next"
                                    onClick={onSave}
                                    disabled={loading || vodLoading || !canSave}
                                >
                                    Calculate &amp; Save
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* blocked screen */}
                {showBlockedScreen ? (
                    <div className="border rounded-lg bg-white p-6">
                        <div className="text-sm text-red-700 space-y-2">
                            <div className="font-medium">
                                {onBlockedMessage || "Required Reserves Calculator is not allowed for this loan program."}
                            </div>

                            <div className="text-xs text-gray-800">
                                <div><b>Loan Program (1401):</b> {loanProgram1401 || "N/A"}</div>
                                <div><b>Loan Program Code:</b> {loanProgramCode || "N/A"}</div>
                                <div><b>Loan Product Name:</b> {selectedProduct || "N/A"}</div>
                                <div><b>Short Term Rental:</b> {shortTermRental || "N/A"}</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="border rounded-lg bg-white">
                            <div className="section-header">Eligible Assets</div>

                            <div className="p-4">
                                {vodLoading ? (
                                    <div className="flex justify-center items-center h-40">
                                        <Spin size="large" />
                                    </div>
                                ) : (
                                    <div>
                                        <div className="mb-3">
                                            <label className="form-label text-sm mb-1 block">Select Borrower Pair</label>

                                            <select
                                                className="form-input text-sm border rounded px-2 py-1 borrower-pair-select"
                                                value={selectedPairLegacyId}
                                                onChange={async (e) => {
                                                    const legacyId = e.target.value;
                                                    setSelectedPairLegacyId(legacyId);

                                                    const selPair = borrowerPairs.find((p) => p.legacyId === legacyId);
                                                    setSelectedPair(selPair || null);

                                                    const appId = selPair?.id || "";
                                                    setApplicationId(appId);

                                                    const ownerAgeYearsMap = getOwnerAgeYearsMap(selPair);
                                                    await loadVodsForApplication(token, loanId, appId, ownerAgeYearsMap, selPair, cashInHand142);
                                                }}
                                                disabled={vodLoading || loading || !borrowerPairs.length}
                                            >
                                                {borrowerPairs.map((pair) => (
                                                    <option key={pair.legacyId} value={pair.legacyId}>
                                                        {formatPairLabel(pair)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="rr-table-wrap">
                                            <Table
                                                size="small"
                                                columns={columns}
                                                dataSource={groupedRows}
                                                pagination={false}
                                                bordered
                                                tableLayout="fixed"
                                                expandable={{
                                                    defaultExpandAllRows: true,
                                                    expandIcon: () => null
                                                }}
                                                rowClassName={(record) => {
                                                    if (record.isGroup) return "asset-group-row";
                                                    if (record.isSubtotal) return "asset-subtotal-row";
                                                    return record.isLastInGroup ? "group-end-row" : "";
                                                }}
                                                summary={() => (
                                                    <Table.Summary>
                                                        <Table.Summary.Row className="required-reserves-summary-row font-semibold">
                                                            {/* Col 1-2 blank */}
                                                            <Table.Summary.Cell index={0} colSpan={2}>
                                                                <b>Total</b>
                                                            </Table.Summary.Cell>

                                                            {/* Col 3 blank */}
                                                            <Table.Summary.Cell index={2} />

                                                            {/* Col 4 Cash Or Market Value */}
                                                            <Table.Summary.Cell index={3}>
                                                                {`$ ${money(totalCashValue)}`}
                                                            </Table.Summary.Cell>

                                                            {/* Col 5 Used in AU (blank) */}
                                                            <Table.Summary.Cell index={4} />

                                                            {/* Col 6 AU Amount */}
                                                            <Table.Summary.Cell index={5}>
                                                                {`$ ${money(totalAuAmount)}`}
                                                            </Table.Summary.Cell>

                                                            {/* Col 7 Verified (blank) */}
                                                            <Table.Summary.Cell index={6} />

                                                            {/* Col 8 Verified Asset */}
                                                            <Table.Summary.Cell index={7}>
                                                                {`$ ${money(totalVerifiedAsset)}`}
                                                            </Table.Summary.Cell>

                                                            {/* Col 9 Allowed Reserves */}
                                                            <Table.Summary.Cell index={8}>
                                                                {`$ ${money(totalAllowedReserve)}`}
                                                            </Table.Summary.Cell>

                                                            {/* Col 10-11 blank */}
                                                            <Table.Summary.Cell index={9} />
                                                            <Table.Summary.Cell index={10} />
                                                            <Table.Summary.Cell index={11}>
                                                                {`$ ${money(totalVerifiedAssetDownPayment)}`}
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    </Table.Summary>
                                                )}
                                            />
                                        </div>

                                        <div className="rr-summary-card border rounded-lg bg-white mt-3 p-4 text-sm">
                                            <div className="grid gap-6" style={{ gridTemplateColumns: "1fr 1fr" }}>

                                                <div className="space-y-4 text-left" style={{ minWidth: 0 }}>

                                                    {/* Source of Reserve */}
                                                    <div>
                                                        <div className="font-semibold mb-2 flex items-center gap-2">
                                                            <span>Source of Reserves</span>

                                                            <Tooltip
                                                                open={reserveCopyOpen}
                                                                title="Summary copied"
                                                                placement="right"
                                                                trigger={[]}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="p-1 rounded hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40"
                                                                    disabled={!reserveSourcesLine}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        copyWithBubble(reserveSourcesLine, setReserveCopyOpen, reserveCopyTimerRef);
                                                                    }}
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                        <path
                                                                            d="M9 9h10v12H9V9Zm-4 6H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"
                                                                            stroke="currentColor"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </Tooltip>


                                                        </div>

                                                        <div
                                                            style={{
                                                                minWidth: 0,
                                                                whiteSpace: "normal",   // 👈 allow wrapping
                                                                wordBreak: "break-word" // 👈 prevent overflow
                                                            }}
                                                            title={reserveSourcesLine}
                                                            className="text-sm"
                                                        >
                                                            {reserveSourcesLine || "No sources selected for reserves."}
                                                        </div>
                                                    </div>

                                                    {/* Source of DownPayment */}
                                                    <div>
                                                        <div className="font-semibold mb-2 flex items-center gap-2">
                                                            <span>Source of Down Payment</span>

                                                            <Tooltip
                                                                open={downCopyOpen}
                                                                title="Summary copied"
                                                                placement="right"
                                                                trigger={[]}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="p-1 rounded hover:bg-gray-100 active:bg-gray-200 disabled:opacity-40"
                                                                    disabled={!downPaymentSourcesLine}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        copyWithBubble(downPaymentSourcesLine, setDownCopyOpen, downCopyTimerRef);
                                                                    }}
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                        <path
                                                                            d="M9 9h10v12H9V9Zm-4 6H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"
                                                                            stroke="currentColor"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </Tooltip>

                                                        </div>

                                                        <div
                                                            style={{
                                                                minWidth: 0,
                                                                whiteSpace: "normal",   // 👈 allow wrapping
                                                                wordBreak: "break-word" // 👈 prevent overflow
                                                            }}
                                                            title={downPaymentSourcesLine}
                                                            className="text-sm"
                                                        >
                                                            {downPaymentSourcesLine || "No sources selected for down payment."}
                                                        </div>
                                                    </div>

                                                </div>


                                                {/* RIGHT SIDE — TOTALS (RIGHT aligned, compact, no big gaps) */}
                                                <div className="text-right">
                                                    <table className="w-full text-sm border-collapse">
                                                        <tbody>
                                                            <tr>
                                                                <td className="pr-3 py-1 font-medium">Cash to From</td>
                                                                <td className="py-1 font-semibold whitespace-nowrap">
                                                                    {moneySigned(cashInHand142)}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-3 py-1 font-medium">Verified Asset</td>
                                                                <td className="py-1 font-semibold whitespace-nowrap">
                                                                    $ {money(totalVerifiedAsset)}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-3 py-1 font-medium">Verified Asset for Reserves</td>
                                                                <td className="py-1 font-semibold whitespace-nowrap">
                                                                    $ {money(totalVerifiedAssetReserve)}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-3 py-1 font-medium">Verified Asset for Down Payment</td>
                                                                <td className="py-1 font-semibold whitespace-nowrap">
                                                                    $ {money(totalVerifiedAssetDownPayment)}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-3 py-1 font-medium">Actual reserves months</td>
                                                                <td className="py-1 font-semibold whitespace-nowrap">
                                                                    {reserveMonths.actual || ""}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-3 py-1 font-medium">Months Reserves Not from cash out</td>
                                                                <td className="py-1 font-semibold whitespace-nowrap">
                                                                    {reserveMonths.noCashout || ""}
                                                                </td>
                                                            </tr>

                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>

                                        {/* bottom save */}
                                        <div className="flex justify-end mt-4">
                                            <div className="flex items-center gap-3">
                                                <div className="save-hint">
                                                    {canSave ? (
                                                        <>
                                                            Click <b>Calculate &amp; Save</b> to store total Verified Asset
                                                        </>
                                                    ) : (
                                                        <span className="text-red-600">{permissionMessage}</span>
                                                    )}
                                                </div>

                                                <button
                                                    className="btn-base btn-next"
                                                    onClick={onSave}
                                                    disabled={loading || vodLoading || !canSave}
                                                >
                                                    Calculate &amp; Save
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                <Toast
                    visible={toastVisible}
                    type={toastType}
                    message={toastMessage}
                    onClose={() => setToastVisible(false)}
                    animationClass={toastAnimation}
                />
            </div>
        </div>
    );
}